"use client";

import { useCallback, useEffect, useState } from "react";
import { type GroupRenderConfig, groupHasVisibleContent } from "./group-visibility";
import type { ChatMessageGroup, ChatStatus } from "./types";

export type UseTransitionGateOptions = {
  readonly groups: ChatMessageGroup[];
  readonly status: ChatStatus;
  readonly config: GroupRenderConfig;
  /** Notified when the user clicks a transition card's continue button. */
  readonly onContinue?: (transitionId: string) => void;
};

export type TransitionGate = {
  /**
   * Transition ids whose cards should be hidden AND whose gates should be
   * lifted. Monotonic — once added, never removed (until the component
   * unmounts). A clicked transition gets added the moment its gated group
   * has a rendered item or streaming ends — whichever is first.
   */
  readonly dismissedTransitionIds: ReadonlySet<string>;
  /**
   * Id of a clicked-but-not-yet-ready transition. Its card should render
   * a spinner to acknowledge the click while the next step lands.
   */
  readonly pendingTransitionId: string | null;
  /** Wire onto the transition card's continue button. */
  readonly onTransitionClick: (transitionId: string) => void;
  /**
   * Index in `groups` of the currently-active gated group (i.e. the one
   * hidden behind an undismissed transition card). -1 when there is no
   * active gate. Only the LAST gated group in the feed is ever active —
   * earlier gated groups are treated as already-passed.
   */
  readonly activeGateIdx: number;
  /**
   * Index of the group that should be treated as the effective "last" one
   * for scroll / last-step / sizing logic. Equals `activeGateIdx - 1`
   * when there's an active gate, else `groups.length - 1`.
   */
  readonly effectiveLastIdx: number;
};

/**
 * Transition-card interaction state for the chat feed.
 *
 * Invariant: `dismissedTransitionIds` is monotonic within a session —
 * once a transition is dismissed it never un-dismisses, even if the
 * conditions that triggered the dismiss (next group has a rendered item /
 * stream ended) later flicker back to false mid-stream.
 *
 * Flow:
 *   click → if next step is already loading (rendered item in gated
 *           group) OR streaming has ended, dismiss immediately;
 *         → else mark as `pending` so the card shows a spinner.
 *   effect → once the ready condition becomes true, commit the pending
 *            dismiss and clear `pending`.
 *
 * "Rendered item" is evaluated with the same `groupHasVisibleContent`
 * predicate the feed uses to decide whether a group's wrapper mounts, so
 * a gated group that only contains items hidden by config (a lone
 * trace-group under `traceMode === "disabled"`, a `thinking` item under
 * `loadingVariant === "disabled"`, etc.) does NOT count — the click
 * stays pending until something user-visible lands.
 */
export function useTransitionGate({
  groups,
  status,
  config,
  onContinue,
}: UseTransitionGateOptions): TransitionGate {
  // Destructure so memo/effect deps depend on primitive values instead of
  // `config`'s identity — `config` is rebuilt every render in ChatFeed.
  const { stageTransition, traceMode, loadingVariant, stageTransitionEnabled } = config;

  const [dismissedTransitionIds, setDismissedTransitionIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [pendingTransitionId, setPendingTransitionId] = useState<string | null>(null);

  const commitDismiss = useCallback((transitionId: string) => {
    setDismissedTransitionIds((prev) => {
      if (prev.has(transitionId)) return prev;
      const next = new Set(prev);
      next.add(transitionId);
      return next;
    });
  }, []);

  // Is the next step already ready to be revealed for this transition?
  const isReadyToDismiss = useCallback(
    (transitionId: string): boolean => {
      if (status !== "streaming") return true;
      const group = groups.find((g) => g.gatedBy === transitionId);
      return (
        !!group &&
        groupHasVisibleContent(
          group,
          { stageTransition, traceMode, loadingVariant },
          dismissedTransitionIds,
        )
      );
    },
    [status, groups, stageTransition, traceMode, loadingVariant, dismissedTransitionIds],
  );

  const onTransitionClick = useCallback(
    (transitionId: string) => {
      if (isReadyToDismiss(transitionId)) {
        commitDismiss(transitionId);
      } else {
        setPendingTransitionId(transitionId);
      }
      onContinue?.(transitionId);
    },
    [isReadyToDismiss, commitDismiss, onContinue],
  );

  // Once the ready condition becomes true for a pending click, commit it.
  useEffect(() => {
    if (!pendingTransitionId) return;
    if (!isReadyToDismiss(pendingTransitionId)) return;
    commitDismiss(pendingTransitionId);
    setPendingTransitionId(null);
  }, [pendingTransitionId, isReadyToDismiss, commitDismiss]);

  // The gate tracks only the LAST group with `gatedBy` in the feed. Any
  // earlier gated group is treated as already-passed — by virtue of more
  // gated content landing after it, the user has moved on. Keeps history
  // replay correct without persisting per-transition dismissal state.
  const lastGatedGroupIdx = stageTransitionEnabled ? groups.findLastIndex((g) => !!g.gatedBy) : -1;
  const lastGatedGroupBy = lastGatedGroupIdx >= 0 ? groups[lastGatedGroupIdx].gatedBy : undefined;
  const activeGateIdx =
    lastGatedGroupBy && !dismissedTransitionIds.has(lastGatedGroupBy) ? lastGatedGroupIdx : -1;
  const effectiveLastIdx = activeGateIdx >= 0 ? activeGateIdx - 1 : groups.length - 1;

  return {
    dismissedTransitionIds,
    pendingTransitionId,
    onTransitionClick,
    activeGateIdx,
    effectiveLastIdx,
  };
}
